import { ROUTES_CHILDREN_LOOKUP } from "@/routes/routes";
import { memo, useEffect, useMemo, useState } from "react";
import { type AppDispatch, type RootState } from "@/store/store";

import { useDispatch, useSelector } from "react-redux";
import { MARKETS, POINT_IN_TIME } from "@/constant/constantsThrougoutProject";
import { dequal } from "dequal";
import { fetchFiltersByMarketId } from "@/store/slices/filters/filters.thunk";
import { fetchSegmentsByMarketId } from "@/store/slices/segments/segments.thunk";
import { fetchAttributeLabels } from "@/store/slices/attributeLabels/attributeLabels.thunk";
import { useLocation, useNavigate } from "react-router-dom";
import { AGE_SEGMENT_LABELS } from "@/constant/ageSegment";
import { updateUrlParams } from "@/utils/url";

const RefreshFilterAndSegmentOnMarketChange: React.FC = () => {
  const location = useLocation();
  const currentRouteDetail = ROUTES_CHILDREN_LOOKUP[location.pathname];
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [isMarketUpdatedAfterFirstTime, setIsMarketUpdatedAfterFirstTime] =
    useState<boolean>(false);

  const userSelectedMode = useSelector(
    (state: RootState) => state.userSelection.mode,
    (prev, next) => dequal(prev, next),
  );

  const allMarketDetails = useSelector(
    (state: RootState) => state.activeGroup.markets,

    (prev, next) => {
      return dequal(prev, next);
    },
  );

  const userSelectedMarkets = useSelector(
    (state: RootState) => {
      if (
        currentRouteDetail?.multipleOptionsForKeys[
          state.userSelection.mode || POINT_IN_TIME
        ]?.includes(MARKETS)
      ) {
        let markets = state.activeGroup.markets.markets;

        const selectedBrandDetail =
          state.userSelection.brands?.length > 0
            ? state.activeGroup.brands.entities[state.userSelection.brands[0]]
            : null;

        if (selectedBrandDetail) {
          return markets?.ids?.filter((marketId) =>
            selectedBrandDetail?.marketIds?.includes(marketId),
          );
        }
      }
      return [
        ...(state.userSelection.markets?.length > 0
          ? [Number(state.userSelection.markets[0])]
          : []),
      ].sort((a, b) => a - b);
    },
    (prev, next) => {
      return dequal(prev, next);
    },
  );

  const moduleToFetchFilter = useMemo(() => {
    return currentRouteDetail?.multipleOptionsForKeys[
      userSelectedMode || POINT_IN_TIME
    ]?.includes(MARKETS)
      ? "GLOBAL"
      : "DETAILED";
  }, [currentRouteDetail?.id]);

  const userSelectedMarketCode: string[] = useMemo(
    () =>
      userSelectedMarkets
        ?.map((marketId: number) => {
          const currMarket = allMarketDetails.markets.entities[marketId];
          return currMarket?.identity;
        })
        .filter((market) => !!market),
    [userSelectedMarkets, allMarketDetails.markets.entities],
  );

  async function getResponse() {
    try {
      const response = await dispatch(
        fetchSegmentsByMarketId({
          marketIds: userSelectedMarkets,
          marketCodes: userSelectedMarketCode,
        }),
      ).unwrap();

      if (!isMarketUpdatedAfterFirstTime) {
        setIsMarketUpdatedAfterFirstTime(true);
      } else if (location.pathname?.includes("segment")) {
        const segments = response?.segments || [];
        const activeSegmentIds = segments
          ?.filter(
            (segment: any) =>
              segment?.isActive &&
              !segment?.segmentMarkets?.includes("Not Applicable"),
          )
          ?.map((segment: any) => segment?.id);
        const ageSegmentDetails = segments?.find(
          (segment: any) =>
            segment?.categoryType === "DEFAULT" &&
            AGE_SEGMENT_LABELS?.includes(segment?.name),
        );
        updateUrlParams(
          {
            segments:
              activeSegmentIds?.length > 0
                ? activeSegmentIds
                : [ageSegmentDetails?.id],
          },
          navigate,
        );
      }
    } catch {
      // fetchSegmentsByMarketId was rejected; skip segment URL update
    }

    dispatch(fetchAttributeLabels({ marketIds: userSelectedMarkets }));
    await dispatch(
      fetchFiltersByMarketId({
        marketIds: userSelectedMarkets,
        module: currentRouteDetail?.multipleOptionsForKeys?.[
          userSelectedMode || "POINT_IN_TIME"
        ]?.includes("markets")
          ? "GLOBAL"
          : "COMPETITIVE",
      }),
    );
  }

  useEffect(() => {
    if (userSelectedMarketCode?.length > 0) {
      getResponse();
    }
  }, [dispatch, userSelectedMarketCode, moduleToFetchFilter]);

  return null;
};

export default memo(RefreshFilterAndSegmentOnMarketChange);
